requestAnimationFrame(() => {
  themeBase.onDOMContentLoadedTrigger(() => {
    scriptController.executeWhenScriptLoaded('search-form.js', () => {
      if (!customElements.get('predictive-search')) {
        class PredictiveSearch extends SearchForm {
          constructor() {
            super();

            this.cachedResults = {};
            this.form = this.querySelector('form');
            this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
            this.predictiveSearchCategory = this.querySelector('[data-predictive-search-category]');
            this.menuDrawer = this.closest('#menu-drawer');
            this.navigationContainer = this.closest('.menu-drawer__navigation-container');
            this.allPredictiveSearchInstances =
              document.querySelectorAll('predictive-search');
            this.popupModule = this.querySelector('popup-module');
            this.isOpen = false;
            this.abortController = new AbortController();
            this.predictiveSearchUnavailable = false;
            this.predictiveSearchUrl = this.getInitialPredictiveSearchUrl();
            this.searchTerm = '';
            this.inlineSearchLayerRoot = this.closest('.shopify-header-section-wrapper');
            this.setupEventListeners();
          }

          setupEventListeners() {
            this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

            this.input.addEventListener('focus', this.onFocus.bind(this));
            this.addEventListener('focusout', this.onFocusOut.bind(this));
            this.addEventListener('keyup', this.onKeyup.bind(this));
            this.addEventListener('keydown', this.onKeydown.bind(this));

            if (this.popupModule) {
              this.popupModule.addEventListener('popupClosed', () => {
                themeSettings.manuallyIgnoreStickyHeader = false;
              });
            }

            if (this.predictiveSearchCategory) {
              this.predictiveSearchCategory.addEventListener('change', this.onChange.bind(this));
            }
          }

          getQuery() {
            return this.input.value.trim();
          }

          getInitialPredictiveSearchUrl() {
            const defaultUrl = routes.predictive_search_url;
            const localizedRoot = window.Shopify?.routes?.root;

            if (localizedRoot && localizedRoot !== '/' && defaultUrl === '/search/suggest') {
              return `${localizedRoot.replace(/\/$/, '')}${defaultUrl}`;
            }

            return defaultUrl;
          }

          getQueryKey(searchTerm, category) {
            return `${this.predictiveSearchUrl || routes.predictive_search_url}|${searchTerm}|${category}`.toLowerCase();
          }

          getLocalizedSearchResults(searchTerm, queryKey) {
            const localizedRoot = window.Shopify?.routes?.root || '/';
            const fallbackSearchUrl = `${localizedRoot.replace(/\/$/, '')}/search`;
            const searchUrl = new URL(routes.search_url || fallbackSearchUrl, window.location.origin);
            searchUrl.searchParams.set('q', searchTerm);
            searchUrl.searchParams.set('type', 'product');
            searchUrl.searchParams.set('options[prefix]', 'last');
            searchUrl.searchParams.set('section_id', 'predictive-search');

            return fetch(searchUrl.toString(), {
                signal: this.abortController.signal
              })
              .then((response) => {
                if (!response.ok) {
                  var error = new Error(response.status);
                  this.close();
                  throw error;
                }

                return response.text();
              })
              .then((text) => this.handleSearchResultsResponse(text, queryKey));
          }

          shouldUseLocalizedSearchFallback() {
            const locale = (document.documentElement.lang || window.Shopify?.locale || '')
              .replace('_', '-')
              .toLowerCase();

            return locale.split('-')[0] === 'ar';
          }

          handleSearchResultsResponse(text, queryKey) {
            if (!text || this.predictiveSearchUnavailable) return;

            const resultsSection = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
            if (!resultsSection) {
              this.close();
              return;
            }

            const resultsMarkup = resultsSection.innerHTML;

            this.allPredictiveSearchInstances.forEach(
              (predictiveSearchInstance) => {
                predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
              }
            );
            this.renderSearchResults(resultsMarkup);
          }

          onChange() {
            super.onChange();
            const newSearchTerm = this.getQuery();
            if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
              this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
            }

            this.updateSearchForTerm(this.searchTerm, newSearchTerm);

            this.searchTerm = newSearchTerm;

            if (!this.searchTerm.length) {
              this.close(true);
              return;
            }

            if (this.predictiveSearchUnavailable) {
              this.close();
              return;
            }

            this.getSearchResults(this.searchTerm);
          }

          onFormSubmit(event) {
            if (!this.getQuery().length && this.predictiveSearchCategory && this.predictiveSearchCategory.value !== '') {
              this.input.value = '*';
            }

            if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
          }

          onFormReset(event) {
            super.onFormReset(event);
            if (super.shouldResetForm()) {
              this.searchTerm = '';
              this.abortController.abort();
              this.abortController = new AbortController();
              this.closeResults(true);
            }
          }

          onFocus() {
            const currentSearchTerm = this.getQuery();

            if (!currentSearchTerm.length || this.predictiveSearchUnavailable) return;

            if (this.searchTerm !== currentSearchTerm) {
              this.onChange();
            } else if (this.getAttribute('results') === 'true') {
              this.open();
            } else {
              this.getSearchResults(this.searchTerm);
            }
          }

          onFocusOut() {
            if (!this.dataset.closeOnfocus) return;
            setTimeout(() => {
              if (!this.contains(document.activeElement)) this.close();
            })
          }

          onKeyup(event) {
            if (!this.getQuery().length) this.close(true);
            event.preventDefault();

            switch (event.code) {
              case 'ArrowUp':
                this.switchOption('up')
                break;
              case 'ArrowDown':
                this.switchOption('down');
                break;
              case 'Enter':
                this.selectOption();
                break;
            }
          }

          onKeydown(event) {
            if (
              event.code === 'ArrowUp' ||
              event.code === 'ArrowDown'
            ) {
              event.preventDefault();
            }
          }

          updateSearchForTerm(previousTerm, newTerm) {
            const searchForTextElement = this.querySelector(
              "[data-predictive-search-search-for-text]"
            );
            const currentButtonText = searchForTextElement?.innerText;
            if (currentButtonText) {
              const previousTermEscaped = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const previousTermMatches = currentButtonText.match(new RegExp(previousTermEscaped, "g")) || [];
              if (previousTermMatches.length > 1) {
                return;
              }
              const newButtonText = currentButtonText.replace(previousTerm, newTerm);
              searchForTextElement.innerText = newButtonText;
            }
          }

          switchOption(direction) {
            if (!this.getAttribute('open')) return;

            const moveUp = direction === 'up';
            const selectedElement = this.querySelector('[aria-selected="true"]');

            const allVisibleElements = Array.from(
              this.querySelectorAll("li, button.predictive-search__item")
            ).filter((element) => element.offsetParent !== null);
            let activeElementIndex = 0;

            if (moveUp && !selectedElement) return;

            let selectedElementIndex = -1;
            let i = 0;

            while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
              if (allVisibleElements[i] === selectedElement) {
                selectedElementIndex = i;
              }
              i++;
            }

            this.statusElement.textContent = "";

            if (!moveUp && selectedElement) {
              activeElementIndex =
                selectedElementIndex === allVisibleElements.length - 1 ?
                0 :
                selectedElementIndex + 1;
            } else if (moveUp) {
              activeElementIndex =
                selectedElementIndex === 0 ?
                allVisibleElements.length - 1 :
                selectedElementIndex - 1;
            }

            if (activeElementIndex === selectedElementIndex) return;

            const activeElement = allVisibleElements[activeElementIndex];

            activeElement.setAttribute('aria-selected', true);
            if (selectedElement) selectedElement.setAttribute('aria-selected', false);

            this.input.setAttribute('aria-activedescendant', activeElement.id);
          }

          selectOption() {
            const selectedOption = this.querySelector(
              '[aria-selected="true"] a, button[aria-selected="true"]'
            );

            if (selectedOption) selectedOption.click();
          }

          disablePredictiveSearch() {
            this.predictiveSearchUnavailable = true;
            this.abortController.abort();
            this.abortController = new AbortController();
            this.close();
            this.removeAttribute('results');
            this.removeAttribute('open');
            this.predictiveSearchResults?.replaceChildren();
            this.input?.removeAttribute('aria-activedescendant');
            this.input?.removeAttribute('aria-controls');
            this.input?.removeAttribute('aria-owns');
            this.input?.removeAttribute('aria-haspopup');
            this.input?.removeAttribute('aria-autocomplete');
          }

          getSearchResults(searchTerm) {
            if (this.predictiveSearchUnavailable) return;

            let category = '';
            const hasCategory = this.predictiveSearchCategory && this.predictiveSearchCategory.value !== '';
            let categoryType;
            let categoryValue;
            this.setLiveRegionLoadingState();

            if (hasCategory) {
              categoryType = this.predictiveSearchCategory.name;
              categoryValue = this.predictiveSearchCategory.value;


              category = '&resources[type]=product'
            }

            this.categoryType = categoryType;
            this.categoryValue = categoryValue;

            const queryKey = this.getQueryKey(searchTerm, category);

            if (this.cachedResults[queryKey]) {
              this.renderSearchResults(this.cachedResults[queryKey]);
              return;
            }

            if (this.shouldUseLocalizedSearchFallback()) {
              this.getLocalizedSearchResults(searchTerm, queryKey);
              return;
            }

            fetch(
                `${this.predictiveSearchUrl}?q=${encodeURIComponent(
                searchTerm
              )}${category}&section_id=predictive-search`, {
                  signal: this.abortController.signal
                }
              )
              .then((response) => {
                if (response.status === 417) {
                  return this.getLocalizedSearchResults(searchTerm, queryKey);
                }

                if (!response.ok) {
                  var error = new Error(response.status);
                  this.close();
                  throw error;
                }

                return response.text();
              })
              .then((text) => this.handleSearchResultsResponse(text, queryKey))
              .catch((error) => {
                if (error?.code === 20) {
                  return;
                }
                this.close();
                throw error;
              });
          }

          setLiveRegionLoadingState() {
            this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
            this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

            this.setLiveRegionText(this.loadingText);
            this.setAttribute('loading', true);

            if (this.menuDrawer) {
              this.navigationContainer.scrollTo(0, 0);
              this.menuDrawer.classList.add('menu-drawer--opened-predictive-search');
            }
          }

          setLiveRegionText(statusText) {
            this.statusElement.setAttribute('aria-hidden', 'false');
            this.statusElement.textContent = statusText;

            setTimeout(() => {
              this.statusElement.setAttribute('aria-hidden', 'true');
            }, 1000);
          }

          renderSearchResults(resultsMarkup) {
            this.predictiveSearchResults.innerHTML = resultsMarkup;

            if (this.categoryType) {
              const productAttribute = this.categoryType === 'filter.p.product_type' ? 'data-product-type' : 'data-vendor';
              const productsList = this.predictiveSearchResults.querySelector('#predictive-search-results-products-list');
              const productGroup = productsList?.closest('.predictive-search__result-group');
              const productsToRemove = productsList?.querySelectorAll(`.predictive-search__list-item:not([${productAttribute}="${this.categoryValue}"])`) || [];

              productsToRemove.forEach((element) => element.remove());

              if (productsList && !productsList.querySelector('.predictive-search__list-item')) {
                productGroup?.remove();
              }
            }


            this.setAttribute('results', true);
            this.predictiveSearchResults.querySelector('.predictive-search__search-for-button button')?.setAttribute('form', this.form.id);
            this.setLiveRegionResults();
            this.open();
          }

          setLiveRegionResults() {
            this.removeAttribute('loading');
            const liveRegionCountValue = this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent;
            if (liveRegionCountValue) {
              this.setLiveRegionText(liveRegionCountValue);
            }
          }

          getResultsMaxHeight() {
            this.resultsMaxHeight = window.innerHeight - themeBounds.get(document.querySelector('.section-header')).bottom;
            return this.resultsMaxHeight;
          }

          setInlineSearchLayerState(isActive) {
            if (!this.dataset.bodyClass) return;

            const activeClass = 'inline-predictive-search-active';

            document.querySelectorAll(`.header-sections-wrapper > .shopify-header-section-wrapper.${activeClass}`).forEach((root) => {
              if (!isActive || root !== this.inlineSearchLayerRoot) {
                root.classList.remove(activeClass);
              }
            });

            if (isActive) {
              this.inlineSearchLayerRoot?.classList.add(activeClass);
            } else {
              this.inlineSearchLayerRoot?.classList.remove(activeClass);
            }
          }

          open() {
            this.setInlineSearchLayerState(true);

            if (this.dataset.bodyClass) {
              document.body.classList.add(this.dataset.bodyClass);
            }

            themeSettings.manuallyIgnoreStickyHeader = true;
            this.setAttribute('open', true);
            this.input.setAttribute('aria-expanded', true);
            this.isOpen = true;
          }

          close(clearSearchTerm = false) {
            themeSettings.manuallyIgnoreStickyHeader = false;
            this.closeResults(clearSearchTerm);
            this.setInlineSearchLayerState(false);

            if (this.dataset.bodyClass) {
              document.body.classList.remove(this.dataset.bodyClass);
            }

            this.isOpen = false;
          }

          closeResults(clearSearchTerm = false) {
            if (clearSearchTerm) {
              this.input.value = '';
              this.removeAttribute('results');
            }
            const selected = this.querySelector('[aria-selected="true"]');

            if (selected) selected.setAttribute('aria-selected', false);

            this.input.setAttribute('aria-activedescendant', '');
            this.removeAttribute('loading');
            this.removeAttribute('open');
            this.input.setAttribute('aria-expanded', false);
            this.resultsMaxHeight = false
            this.predictiveSearchResults.removeAttribute('style');

            if (this.menuDrawer) {
              this.menuDrawer.classList.remove('menu-drawer--opened-predictive-search');
            }
          }
        }

        customElements.define('predictive-search', PredictiveSearch);
      }
    });
  });
});